import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import * as Icons from "lucide-react";
import { ExternalLink, Landmark, Sprout } from "lucide-react";

const Schemes = () => {
  const { t } = useLang();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    api
      .get("/schemes")
      .then((r) => setSchemes(r.data.schemes))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const s = new Set(schemes.map((sc) => sc.category.split(" · ")[0]));
    return Array.from(s);
  }, [schemes]);

  const filtered = useMemo(() => {
    if (!category) return schemes;
    return schemes.filter((s) => s.category.startsWith(category));
  }, [schemes, category]);

  return (
    <div data-testid="schemes-page" className="bg-[#FDFBF7]">
      <section className="relative overflow-hidden border-b border-stone-200 bg-gradient-to-b from-[#F4F1EA] to-[#FDFBF7]">
        <div className="container-gc px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-stone-200 text-xs font-medium text-[#1E4D2B] mb-5">
              <Sprout size={14} /> Sarkari Yojana
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
              Government Schemes for Farmers
            </h1>
            <p className="mt-4 text-lg text-stone-700 leading-relaxed max-w-2xl">
              Central & state schemes for garlic and other crop farmers — PM-KISAN, KCC, PMFBY, KUSUM and more. Direct application links included.
            </p>
          </div>
        </div>
      </section>

      <section className="container-gc px-4 sm:px-6 py-8">
        {/* Category filter chips */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
          <button
            data-testid="scheme-cat-all"
            onClick={() => setCategory("")}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              category === "" ? "bg-[#1E4D2B] text-white" : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-100"
            }`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`scheme-cat-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                category === c ? "bg-[#1E4D2B] text-white" : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="card-gc p-12 text-center text-stone-500">{t.common.loading}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((s, idx) => {
              const Icon = Icons[s.icon] || Landmark;
              return (
                <article
                  key={s.id}
                  data-testid={`scheme-card-${idx}`}
                  className="card-gc p-6 flex flex-col hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 rounded-xl bg-[#1E4D2B] text-white flex items-center justify-center">
                      <Icon size={22} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#F4F1EA] text-stone-600">
                      {s.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-stone-900 leading-tight">{s.name}</h3>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#F59E0B] self-start">
                    {s.benefit}
                  </div>
                  <p className="mt-3 text-stone-600 text-sm leading-relaxed">{s.summary}</p>

                  {s.eligibility?.length > 0 && (
                    <details className="mt-4 group">
                      <summary className="text-xs font-semibold uppercase tracking-wider text-stone-500 cursor-pointer hover:text-stone-900 transition-colors">
                        Eligibility ▾
                      </summary>
                      <ul className="mt-2 space-y-1.5 text-sm text-stone-700 list-disc list-inside">
                        {s.eligibility.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <a
                    href={s.apply_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`scheme-apply-${idx}`}
                    className="btn-primary mt-5 justify-center !h-11"
                  >
                    Apply / Learn More <ExternalLink size={14} />
                  </a>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Schemes;
